import React from "react";
import { FaExternalLinkAlt } from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "src/context/theme.context";
import { appendSearchToPath } from "src/customHooks/usePaginatedTableSearchParams";
import { RecruiterKind, RecruiterRow } from "../types/recruiter.entity";
import { formatDate } from "../utils/recruiter.labels";
import { RecruiterTableSkeleton } from "./RecruiterTableSkeleton";

type Props = {
  kind: RecruiterKind;
  data: RecruiterRow[];
  isLoading: boolean;
};

const SUBJECT_HEADING: Record<RecruiterKind, string> = {
  applications: "Vacancy",
  "help-requests": "Vacancy",
  searches: "Looking for",
};

const SUMMARY_HEADING: Record<RecruiterKind, string> = {
  applications: "Motivation",
  "help-requests": "Questions",
  searches: "",
};

/**
 * What the Open button says it opens, in its tooltip.
 *
 * The record kind is in the tab, not in the row, so a reader who has scrolled
 * a long table gets it named again where they act on it.
 */
const OPEN_NOUN: Record<RecruiterKind, string> = {
  applications: "application",
  "help-requests": "help request",
  searches: "saved search",
};

const EMPTY_TEXT: Record<RecruiterKind, string> = {
  applications: "No applications yet. They appear here when a learner finishes applying with the Taaly recruiter.",
  "help-requests": "No help requests yet. They appear here when a learner finishes asking about a vacancy.",
  searches: "No saved searches yet. They appear here when a learner asks the recruiter to look for work.",
};

export const RecruiterTable: React.FC<Props> = ({ kind, data, isLoading }) => {
  const { isDarkMode } = useTheme();
  const { search } = useLocation();

  const wrap = isDarkMode ? "border border-gray-700 divide-y divide-gray-700" : "border border-gray-200 divide-y divide-gray-200";
  const rowBg = isDarkMode ? "hover:bg-gray-800/50" : "odd:bg-white even:bg-gray-50 hover:bg-gray-100";
  const borderT = isDarkMode ? "border-t border-gray-700" : "border-t border-gray-200";
  const cell = isDarkMode ? "text-gray-200" : "text-gray-800";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const showSummary = kind !== "searches";

  return (
    <div className={`overflow-x-auto mb-6 rounded-xl overflow-hidden ${wrap}`}>
      <table className="min-w-[860px] w-full text-xs">
        <thead className="bg-gray-900 text-white">
          <tr>
            <th className="px-4 py-3 text-left uppercase tracking-wider">Received</th>
            <th className="px-4 py-3 text-left uppercase tracking-wider">Learner</th>
            <th className="px-4 py-3 text-left uppercase tracking-wider">{SUBJECT_HEADING[kind]}</th>
            {showSummary && <th className="px-4 py-3 text-left uppercase tracking-wider">{SUMMARY_HEADING[kind]}</th>}
            <th
              className="px-4 py-3 text-left uppercase tracking-wider"
              title="The recruiter deletes each record, CV included, 180 days after the learner's last activity."
            >
              Deleted on
            </th>
            <th className="px-4 py-3 text-left uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <RecruiterTableSkeleton rows={8} showSummary={showSummary} />
          ) : !data.length ? (
            <tr>
              <td colSpan={showSummary ? 6 : 5} className={`px-4 py-10 text-center text-sm ${muted}`}>
                {EMPTY_TEXT[kind]}
              </td>
            </tr>
          ) : (
            data.map((row) => (
              <tr key={row.id} className={`text-left ${rowBg}`}>
                <td className={`px-4 py-3 ${borderT} ${cell} whitespace-nowrap font-semibold`}>
                  {formatDate(row.createdAt)}
                </td>
                <td className={`px-4 py-3 ${borderT} ${cell}`}>
                  {row.learnerName ? (
                    <>
                      <Link to={`/users/${row.userId}`} className="font-medium no-underline hover:underline">
                        {row.learnerName}
                      </Link>
                      {row.learnerEmail && <div className={muted}>{row.learnerEmail}</div>}
                    </>
                  ) : (
                    <>
                      <span className="font-mono">{row.userId}</span>
                      <div className={muted}>User not found</div>
                    </>
                  )}
                </td>
                <td className={`px-4 py-3 ${borderT} ${cell}`}>
                  <div className="font-medium">{row.subject}</div>
                  <div className={row.jobListed ? muted : "text-amber-600"}>{row.subjectDetail}</div>
                </td>
                {showSummary && (
                  <td className={`px-4 py-3 ${borderT} ${cell} max-w-sm`}>
                    <span className="line-clamp-2">{row.summary}</span>
                  </td>
                )}
                <td className={`px-4 py-3 ${borderT} ${muted} whitespace-nowrap`}>{formatDate(row.expiresAt)}</td>
                <td className={`px-4 py-3 ${borderT} whitespace-nowrap`}>
                  <Link
                    to={appendSearchToPath(`/recruiter/${kind}/${row.id}`, search)}
                    title={`Open this ${OPEN_NOUN[kind]}`}
                    className="inline-flex items-center gap-2 bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white no-underline hover:bg-emerald-700"
                  >
                    <FaExternalLinkAlt />
                    Open
                  </Link>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
